import React, { useState, useEffect, useCallback } from 'react'
import './styles.css'
import { Drawer, Box, Typography, Tabs, Tab } from '@mui/material'
import { useLocation } from 'react-router-dom'

import RadioSelect from 'src/common/components/RadioSelect'
import LoadingIndicator from 'src/common/components/LoadingIndicator'
import CreateTopicButton from './CreateTopicButton'
import MicroTrainingCard from './MicroTrainingCard'
import MicroTrainingPreview from './MicroTrainingPreview'
import { useTrainingTopics } from '../../hooks/useTrainingTopics'
import { useTrainingModules } from '../../hooks/useTrainingModules'
import ModulesPreview from './CreateEditModule/ModulesPreview'

import { debounce } from '../manual-simulation-v2/utils'
import SearchField from './SearchField'
import { EmptyPage } from './emptyPage'
import GenerateModuleModal from './GenerateModuleModal'

export default function Microtraining() {
  const location = useLocation()
  const [openDrawer, setOpenDrawer] = useState(false)
  const [generateModuleOpen, setGenerateModuleOpen] = useState(false)
  const [selectedTopicId, setSelectedTopicId] = useState(null)
  const [topicType, setTopicType] = useState(location.state?.activeTab === 1 ? 'CUSTOM' : 'INTERNAL')
  const [searchText, setSearchText] = useState('')
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 0)

  const { trainings, initialCount, isFetching, language, handleActivateTraining, handleLanguageChange, fetchTopics } =
    useTrainingTopics()

  const selectedTopic = trainings?.find(el => el.trainingId === selectedTopicId) || null
  const selectedTopicType = selectedTopic?.type || null

  const { modules, modulesLoading, isPreviewOpen, modulePreview, handleModulePreview, closeModulePreview } =
    useTrainingModules(selectedTopicId, language, selectedTopicType)

  const onSelectMicro = id => {
    setSelectedTopicId(id)
    setOpenDrawer(true)
  }

  const handleDrawerClose = () => setOpenDrawer(false)

  const getSelectedTopic = () => trainings?.find(el => el.trainingId === selectedTopicId)

  const handleModulePreviewOverride = moduleId => {
    handleModulePreview(moduleId)
  }

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue)
    setTopicType(newValue === 0 ? 'INTERNAL' : 'CUSTOM')
  }

  const memoizedDebouncedFetchTopics = useCallback(
    debounce((currentSearchText, currentTopicType) => {
      fetchTopics(currentTopicType, currentSearchText)
    }, 1000),
    [fetchTopics]
  )

  useEffect(() => {
    fetchTopics(topicType, searchText)
  }, [topicType, fetchTopics])

  const handleSearch = value => {
    memoizedDebouncedFetchTopics(value, topicType)
  }

  const tabsConfig = [
    {
      label: 'System Topics',
      content: null,
      value: 0
    },
    {
      label: 'Custom Topics',
      content: null,
      value: 1
    }
  ]

  const onGenerateClick = () => {
    setGenerateModuleOpen(true)
  }

  return (
    <div style={{ backgroundColor: '#F8F8FB', padding: '24px' }}>
      <div className='row row-center'>
        <Box display='flex' alignItems='center' columnGap={2}>
          <Typography fontSize={'24px'} fontWeight={900} fontFamily={'Satoshi'}>
            Training Topics
          </Typography>
        </Box>
        <div className='spacer'></div>
        <Box display={'flex'} columnGap={'32px'} alignItems='center'>
          {!(activeTab === 1 && initialCount === 0) && (
            <SearchField searchText={searchText} setSearchText={setSearchText} onSearch={handleSearch} />
          )}
          <RadioSelect
            isHidden={activeTab === 1}
            style={{ height: '40px' }}
            options={[
              'Dutch',
              'English',
              'English (UK)',
              'French',
              'German',
              'Italian',
              'Polish',
              'Portuguese',
              'Russian',
              'Slovak',
              'Slovenian',
              'Spanish',
              'Ukrainian'
            ]}
            value={language}
            onSelectOption={handleLanguageChange}
          />
          <CreateTopicButton size={'small'} variant={'contained'} isHidden={activeTab === 0} />
        </Box>
      </div>
      <Box sx={{ mt: 2, mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: '1px solid #E7E8EA',
            '& .MuiTabs-indicator': {
              backgroundColor: '#1B1B26'
            }
          }}
        >
          {tabsConfig.map((tab, index) => (
            <Tab
              key={index}
              label={tab.label}
              value={tab.value}
              sx={{
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                color: '#6123FF',
                padding: '12px 16px',
                fontFamily: 'Plus Jakarta Sans',
                '&.Mui-selected': {
                  color: '#1B1B26'
                }
              }}
            />
          ))}
        </Tabs>
      </Box>
      <div className='micro-training-items row'>
        {isFetching ? (
          <LoadingIndicator />
        ) : (
          <>
            {trainings && trainings.length > 0
              ? trainings.map(el => (
                  <MicroTrainingCard
                    onGenerateClick={() => {
                      setSelectedTopicId(el.trainingId)
                      onGenerateClick()
                    }}
                    onActivate={handleActivateTraining}
                    onSelect={() => onSelectMicro(el.trainingId)}
                    key={el.trainingId}
                    item={el}
                  />
                ))
              : !searchText &&
                !isFetching &&
                initialCount === 0 && (
                  <Box width='100%' display='flex' justifyContent='center' mt={4}>
                    <EmptyPage
                      action={<CreateTopicButton size={'small'} variant={'contained'} isHidden={activeTab === 0} />}
                    />
                  </Box>
                )}
          </>
        )}
      </div>
      {generateModuleOpen && (
        <GenerateModuleModal
          topicId={selectedTopicId}
          open={generateModuleOpen}
          handleClose={() => setGenerateModuleOpen(false)}
        />
      )}
      <Drawer anchor='right' open={openDrawer} onClose={handleDrawerClose}>
        <MicroTrainingPreview
          onGenerateClick={onGenerateClick}
          modules={modules}
          language={language}
          modulesLoading={modulesLoading}
          item={getSelectedTopic()}
          onClose={handleDrawerClose}
          onModulePreview={handleModulePreviewOverride}
        />
      </Drawer>
      {isPreviewOpen && modulePreview && (
        <ModulesPreview
          open={isPreviewOpen}
          onClose={closeModulePreview}
          modules={modulePreview?.steps}
          theme={modulePreview.theme || 'THEME_1'}
        />
      )}
    </div>
  )
}
